import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Sun } from "lucide-react";
import { toast } from "sonner";
import { PaperLight, fetchPaperLightSetting } from "../PaperLight";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const client = axios.create({ baseURL: API, withCredentials: true });
client.interceptors.request.use((cfg) => {
  const t = typeof window !== "undefined" ? localStorage.getItem("mra_jwt") : null;
  if (t) cfg.headers = { ...(cfg.headers || {}), Authorization: `Bearer ${t}` };
  return cfg;
});

const PRESETS = [
  { label: "Calm", value: 0.5 },
  { label: "Default", value: 1 },
  { label: "Bright", value: 1.6 },
];

/**
 * PaperLightDial — nudge the "Paper & Light" WebGL ground brighter or calmer,
 * or switch it off, from /admin. Live preview swatch; saves on release.
 */
export const PaperLightDial = () => {
  const [setting, setSetting] = useState(null);
  const [value, setValue] = useState(1);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    client.get("/site/paper-light")
      .then((r) => { setSetting(r.data); setValue(r.data.intensity); })
      .catch(() => setSetting({ enabled: true, intensity: 1 }));
  }, []);

  const save = async (patch) => {
    setSaving(true);
    try {
      const r = await client.put("/admin/site/paper-light", patch);
      setSetting(r.data);
      setValue(r.data.intensity);
      fetchPaperLightSetting(true);
      toast.success(
        patch.enabled === false ? "Paper light switched off." : patch.enabled === true ? "Paper light back on." : `Paper light set to ${Math.round(r.data.intensity * 100)}%.`,
        { description: "Applies on the next page load, sitewide." },
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save the light setting.");
    } finally {
      setSaving(false);
    }
  };

  if (!setting) {
    return <div className="rounded-2xl border border-border bg-bone px-5 py-4" data-testid="paper-light-dial"><Loader2 className="h-4 w-4 animate-spin text-olive" /></div>;
  }

  const pct = Math.round(value * 100);
  return (
    <div className="rounded-2xl border border-border bg-bone px-5 py-4" data-testid="paper-light-dial">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-yellow text-ink">
            <Sun className="h-4 w-4" strokeWidth={2} />
          </span>
          <div>
            <p className="text-[13px] font-semibold uppercase tracking-[0.18em] text-olive">Paper &amp; light</p>
            <p className="mt-0.5 text-[13px] text-olive">The slow daylight on the homepage hero and page headers. Nudge it brighter or calmer; changes apply on the next page load.</p>
          </div>
        </div>
        <button
          type="button" role="switch" aria-checked={setting.enabled} disabled={saving}
          onClick={() => save({ enabled: !setting.enabled })}
          data-testid="paper-light-toggle"
          className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-[12px] font-semibold transition disabled:opacity-60 ${setting.enabled ? "border-ink bg-ink text-sand hover:bg-forest" : "border-ink/25 bg-bone text-ink hover:border-ink"}`}
        >
          <span className={`h-2 w-2 rounded-full ${setting.enabled ? "bg-yellow" : "bg-ink/30"}`} />
          {setting.enabled ? "On" : "Off"}
        </button>
      </div>

      <div className={`mt-4 grid gap-4 sm:grid-cols-[1fr_180px] ${setting.enabled ? "" : "opacity-50"}`}>
        <label className="block">
          <span className="flex items-center justify-between text-[11px] font-semibold uppercase tracking-[0.18em] text-olive">
            Intensity <span className="font-mono text-ink" data-testid="paper-light-value">{pct}%</span>
          </span>
          <span className="mt-2 flex items-center gap-2 text-[10px] uppercase tracking-[0.14em] text-olive/80">
            <span className="w-8">still</span>
            <input
              type="range" min="0" max="2" step="0.1" value={value} disabled={saving || !setting.enabled}
              onChange={(e) => setValue(Number(e.target.value))}
              onMouseUp={(e) => save({ intensity: Number(e.target.value) })}
              onTouchEnd={(e) => save({ intensity: Number(e.target.value) })}
              onKeyUp={(e) => save({ intensity: Number(e.target.value) })}
              className="w-full accent-ink" aria-label="Paper light intensity" data-testid="paper-light-slider"
            />
            <span className="w-8 text-right">bright</span>
          </span>
          <span className="mt-3 flex flex-wrap gap-2">
            {PRESETS.map((p) => (
              <button key={p.label} type="button" disabled={saving || !setting.enabled} onClick={() => { setValue(p.value); save({ intensity: p.value }); }}
                data-testid={`paper-light-preset-${p.label.toLowerCase()}`}
                className={`rounded-full border px-3 py-1 text-[12px] font-semibold transition disabled:opacity-60 ${Math.abs(setting.intensity - p.value) < 0.01 ? "border-ink bg-ink text-sand" : "border-ink/25 bg-bone text-ink hover:border-ink"}`}>
                {p.label}
              </button>
            ))}
          </span>
        </label>
        <div className="relative h-[110px] overflow-hidden rounded-md border border-border bg-sand" data-testid="paper-light-preview" aria-hidden="true">
          {setting.enabled && <PaperLight intensity={value} />}
          <span className="absolute bottom-2 left-3 z-10 font-serif text-lg italic text-ink/70">live preview</span>
        </div>
      </div>
    </div>
  );
};
